import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  FiBell as Bell,
  FiCreditCard as CreditCard,
  FiUsers as UsersIcon,
  FiCloud as CloudIcon,
  FiHelpCircle as HelpCircle
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import HelpPanel from '../../../components/common/HelpPanel';
import SettingsHelp from '../../../features/settings/help';

interface SettingsNavItem {
  path: string;
  label: string;
  icon: IconType;
}

const SettingsLayout: React.FC = () => {
  const location = useLocation();
  const [showHelpPanel, setShowHelpPanel] = useState(false);

  // Settings navigation items
  const settingsNavItems: SettingsNavItem[] = [
    { path: '/settings/subscriptions', label: 'Subscriptions', icon: CreditCard },
    { path: '/settings/notifications', label: 'Notifications', icon: Bell },
    { path: '/settings/access-management', label: 'Access Management', icon: UsersIcon },
    { path: '/settings/aws-environments', label: 'AWS Environments', icon: CloudIcon },
  ];

  const isActive = (path: string): boolean => {
    // Check if current path matches the nav item path
    if (path === '/settings/subscriptions') {
      return location.pathname === path || location.pathname === '/settings';
    }
    return location.pathname === path;
  };

  // Get the active tab ID for help context
  const getActiveTab = (): string => {
    const path = location.pathname;
    if (path.includes('/notifications')) return 'notifications';
    return 'subscriptions';
  };

  return (
    <div>
      {/* Settings Sub-Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-8" aria-label="Settings navigation">
              {settingsNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`${
                      isActive(item.path)
                        ? 'border-blue-500 text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            {/* Help button */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowHelpPanel(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Help"
                aria-label="Help"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Content */}
      <Outlet />

      {/* Help Panel */}
      <HelpPanel
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
        title="Settings Help"
      >
        <SettingsHelp activeTab={getActiveTab()} />
      </HelpPanel>
    </div>
  );
};

export default SettingsLayout;
